import React, { useState, useEffect, useCallback } from "react";
import { Button, Dialog, Label, Input, Combobox } from "@/components/ui";
import { Form, Fan, FormServices, FormRequest } from "@/services/bedrock";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { usePaginatedCombobox } from "@/utils";
import { WandSparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { FormPersonalizationHelpDialog } from "../../Forms/components/FormPersonalizationHelpDialog";
import { FormRequestSuccessView } from "../../Forms/components/FormRequestSuccessView";
import { FormRequestSettingsView } from "../../Forms/components/FormRequestSettingsView";

interface FanFormRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fan: Fan | null;
}

const POLLING_MESSAGES = [
  "Tailoring your custom feedback experience...",
  "Generating personalized URL and shortcode...",
  "Coaching our AI on player and fan context...",
  "Translating prompts and finalizing layout...",
  "Synchronizing variables with your SocialCoach dashboard...",
  "Almost ready! Finalizing the feedback request link...",
];

export function FanFormRequestDialog({ open, onOpenChange, fan }: FanFormRequestDialogProps) {
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [generatedRequest, setGeneratedRequest] = useState<FormRequest | null>(null);
  const [loadingMsg, setLoadingMsg] = useState(POLLING_MESSAGES[0]);

  // Settings states
  const [isTest, setIsTest] = useState(false);
  const [useAiPersonalization, setUseAiPersonalization] = useState(false);
  const [contextInstructions, setContextInstructions] = useState("");
  const [internalIdentifier, setInternalIdentifier] = useState("");
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  // Get forms list
  const getForms = async (search: string | undefined, page: number) => {
    const result = await FormServices.getForms({ search, page, perPage: 25 });
    return {
      ...result,
      content: result.content.map(form => ({
        id: form.id,
        label: form.displayName || "Untitled Form",
        original: form,
      })),
    };
  };

  const forms = usePaginatedCombobox(getForms, false);

  // Reset dialog state when opening
  useEffect(() => {
    if (open) {
      setCurrentStep(1);
      setIsTest(false);
      setUseAiPersonalization(false);
      setContextInstructions("");
      setInternalIdentifier("");
      setGeneratedRequest(null);
      setLoadingMsg(POLLING_MESSAGES[0]);
      forms.onValueChange(null as any, undefined);
    }
  }, [open]);

  // Loading messages rotation during polling
  useEffect(() => {
    let msgInterval: any = null;
    if (currentStep === 3) {
      msgInterval = setInterval(() => {
        setLoadingMsg(prev => {
          const remaining = POLLING_MESSAGES.filter(m => m !== prev);
          return remaining[Math.floor(Math.random() * remaining.length)];
        });
      }, 3500);
    }
    return () => {
      if (msgInterval) clearInterval(msgInterval);
    };
  }, [currentStep]);

  // Polling loop for request status
  useEffect(() => {
    let pollInterval: any = null;

    if (currentStep === 3 && generatedRequest?.id) {
      const poll = async () => {
        try {
          const result = await FormServices.getFormRequests({
            formRequestId: generatedRequest.id,
          } as any);
          const request = result.content?.find(r => r.id === generatedRequest.id);

          if (request) {
            if (request.status === "ready") {
              setGeneratedRequest(request);
              setCurrentStep(4); // Success step!
            }
          }
        } catch (err) {
          console.error("Error polling form request", err);
        }
      };

      poll();
      pollInterval = setInterval(poll, 3000);
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [currentStep, generatedRequest?.id]);

  const createRequestMutation = useMutation({
    mutationFn: (params: any) => {
      if (!forms.value?.id) throw new Error("No form ID found");
      return FormServices.createFormRequest(Number(forms.value.id), params);
    },
    onSuccess: (data: FormRequest) => {
      queryClient.invalidateQueries({ queryKey: ["formRequests"] });
      setGeneratedRequest(data);
      setCurrentStep(3); // Switch to polling step!
    },
    onError: err => {
      console.error("Failed to create form request", err);
      alert("Error generating form link. Please try again.");
    },
  });

  const handleCreateRequest = () => {
    if (!fan || !forms.value) return;

    const selectedForm = forms.value.original;
    const isAiAllowed = selectedForm?.allowAiPersonalization;

    const params: any = {
      isTest,
      useAiPersonalization: isAiAllowed ? useAiPersonalization : false,
      internalIdentifier: internalIdentifier || undefined,
      fanId: fan.id,
    };

    if (fan.player) {
      params.playerId = fan.player.id;
    }
    if (isAiAllowed && useAiPersonalization && contextInstructions) {
      params.contextInstructions = contextInstructions;
    }

    createRequestMutation.mutate(params);
  };

  const hasSelectedForm = !!forms.value;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg">
      <Dialog.Header>
        <Dialog.Title>
          {currentStep === 3 && "Personalizing Link"}
          {currentStep === 4 && "Form Link Ready"}
          {currentStep <= 2 && "Generate Form Link"}
        </Dialog.Title>
        <Dialog.Description>
          {currentStep === 3 && "Please wait while our AI system customizes the request flow..."}
          {currentStep === 4 && "Share this unique URL with your contacts to gather feedback."}
          {currentStep <= 2 && fan && `Create a personalized, unique feedback link for ${fan.displayName}.`}
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.Content className="max-h-[65vh] overflow-y-auto">
        {/* Step Stepper Header (Hidden during polling/success) */}
        {currentStep <= 2 && (
          <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-6 select-none">
            {[
              { label: "Select Form", num: 1 },
              { label: "Settings", num: 2 },
            ].map((step, idx, arr) => (
              <React.Fragment key={step.num}>
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      "size-6 rounded-full flex items-center justify-center text-xs font-bold font-mono border transition-all duration-200",
                      currentStep === step.num
                        ? "bg-primary border-primary text-white ring-4 ring-primary/10"
                        : currentStep > step.num
                        ? "bg-emerald-500 border-emerald-500 text-white"
                        : "bg-slate-50 border-slate-200 text-slate-400"
                    )}
                  >
                    {currentStep > step.num ? "✓" : step.num}
                  </div>
                  <span
                    className={cn(
                      "text-xs font-bold font-sans",
                      currentStep === step.num
                        ? "text-slate-800"
                        : currentStep > step.num
                        ? "text-emerald-600"
                        : "text-slate-400"
                    )}
                  >
                    {step.label}
                  </span>
                </div>
                {idx < arr.length - 1 && (
                  <div
                    className={cn(
                      "h-0.5 flex-1 mx-4 transition-all duration-200",
                      currentStep > step.num ? "bg-emerald-200" : "bg-slate-100"
                    )}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* STEP 1: CHOOSE FORM */}
        {currentStep === 1 && (
          <div className="flex flex-col gap-4 py-2 animate-in fade-in duration-200">
            <div className="flex flex-col gap-1 mb-2">
              <h3 className="text-sm font-bold text-slate-800 font-sans">Choose Feedback Form</h3>
              <p className="text-xs text-muted-foreground leading-normal">
                Select the feedback form you want to send to {fan?.displayName}.
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <Label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Select Form</Label>
              <Combobox {...forms} placeholder="Search for a Form..." className="w-full" />
            </div>
          </div>
        )}

        {/* STEP 2: SETTINGS */}
        {currentStep === 2 && (
          <FormRequestSettingsView
            isTest={isTest}
            onIsTestChange={setIsTest}
            useAiPersonalization={useAiPersonalization}
            onUseAiPersonalizationChange={setUseAiPersonalization}
            contextInstructions={contextInstructions}
            onContextInstructionsChange={setContextInstructions}
            internalIdentifier={internalIdentifier}
            onInternalIdentifierChange={setInternalIdentifier}
            contextPlaceholder={`e.g. ${
              fan?.displayName || "This fan"
            } closed on a VA loan. Focus on their home buying experience and how we helped navigate their military benefit options...`}
            onHelpClick={() => setHelpDialogOpen(true)}
            hideAiPersonalization={!forms.value?.original?.allowAiPersonalization}
            playerLabel={fan?.player?.fullName ?? undefined}
            fanLabel={fan?.displayName ?? undefined}
          />
        )}

        {/* STEP 3: POLLING LOADING STATE */}
        {currentStep === 3 && (
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center animate-in fade-in duration-500 select-none">
            <div className="relative mb-8">
              <div className="absolute inset-0 scale-150 bg-primary/10 rounded-full blur-3xl animate-pulse" />
              <div className="relative z-10 flex items-center justify-center size-20 bg-white rounded-2xl shadow-xl border border-primary/10">
                <WandSparkles className="size-10 text-primary animate-pulse" />
              </div>
            </div>

            <div className="flex flex-col items-center gap-2 mb-6">
              <h3 className="text-xl font-bold text-slate-800 tracking-tight">Personalizing Form Link...</h3>
              <p className="text-muted-foreground text-xs max-w-xs mx-auto font-sans italic transition-all duration-300 min-h-[32px] flex items-center justify-center">
                "{loadingMsg}"
              </p>
            </div>

            <div className="w-40 bg-muted rounded-full h-1 overflow-hidden">
              <div className="bg-primary h-1 rounded-full animate-pulse w-full" />
            </div>
            <span className="text-xs text-muted-foreground italic mt-6 leading-normal block max-w-[250px]">
              Personalizing each link dynamically can take up to a minute. Please don't close this window.
            </span>
          </div>
        )}

        {/* STEP 4: SUCCESS & COPY STATE */}
        {currentStep === 4 && generatedRequest && (
          <FormRequestSuccessView
            url={generatedRequest.url}
            summaryItems={[
              {
                label: "Selected Form",
                value: forms.value?.label || "Unknown Form",
              },
              {
                label: "Fan Contact",
                value: fan?.displayName || "Unknown Fan",
              },
              {
                label: "AI Personalized",
                value: (
                  <span className={cn("font-semibold", useAiPersonalization ? "text-primary" : "text-slate-800")}>
                    {useAiPersonalization ? "Enabled" : "Disabled"}
                  </span>
                ),
              },
              ...(internalIdentifier
                ? [
                    {
                      label: "Internal ID",
                      value: <span className="font-mono text-slate-700">{internalIdentifier}</span>,
                    },
                  ]
                : []),
            ]}
          />
        )}
      </Dialog.Content>

      <Dialog.Footer>
        <div className="flex justify-between w-full items-center select-none">
          <div>
            {currentStep > 1 && currentStep <= 2 && (
              <Button variant="ghost" onClick={() => setCurrentStep(prev => prev - 1)} type="button">
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {currentStep <= 2 && (
              <Button variant="outline" onClick={() => onOpenChange(false)} type="button">
                Cancel
              </Button>
            )}

            {currentStep === 1 && (
              <Button onClick={() => setCurrentStep(2)} type="button" disabled={!hasSelectedForm}>
                Next
              </Button>
            )}

            {currentStep === 2 && (
              <Button onClick={handleCreateRequest} loading={createRequestMutation.isPending} type="button">
                Create Link
              </Button>
            )}

            {currentStep === 4 && (
              <Button onClick={() => onOpenChange(false)} type="button">
                Done
              </Button>
            )}
          </div>
        </div>
      </Dialog.Footer>

      {/* Form Personalization Help Dialog */}
      <FormPersonalizationHelpDialog open={helpDialogOpen} onOpenChange={setHelpDialogOpen} />
    </Dialog>
  );
}
